import React, { useMemo } from "react";
import { HIGHLIGHT_CLASS } from "@/lib/fitment";

// Escape special regex chars so terms like "c++", ".net", "node.js" don't break.
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Renders `text` with any occurrence of the given `skills` wrapped in a
 *  coloured span keyed to that skill's match bucket (blue/green/amber/red).
 *  Falls back to a plain paragraph when highlighting is disabled or no skills. */
export default function HighlightedDescription({ text, skills, active }) {
  const parts = useMemo(() => {
    if (!active || !text || !skills?.length) return null;
    // Sort longest-first so "data governance" wins over "governance".
    const rules = skills
      .filter((s) => s?.name && typeof s.name === "string")
      .slice()
      .sort((a, b) => b.name.length - a.name.length);
    if (!rules.length) return null;
    // Build one big regex: (term1|term2|...) with word-boundary-ish lookarounds.
    const pattern = new RegExp(
      `(?<![a-z0-9])(${rules.map((r) => escapeRe(r.name)).join("|")})(?![a-z0-9])`,
      "gi",
    );
    const out = [];
    let last = 0;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      if (m.index > last) out.push(text.slice(last, m.index));
      const raw = m[0];
      const lc = raw.toLowerCase();
      const rule = rules.find((r) => r.name.toLowerCase() === lc);
      const cls = HIGHLIGHT_CLASS[rule?.match] || "";
      out.push(
        <mark key={`${m.index}-${raw}`} className={`${cls} font-medium`} data-testid={`desc-hl-${rule?.match || "unknown"}`}>
          {raw}
        </mark>,
      );
      last = m.index + raw.length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  }, [text, skills, active]);

  return (
    <div className="prose prose-neutral dark:prose-invert max-w-none mb-8">
      <p className="text-base leading-relaxed whitespace-pre-wrap" data-testid="job-description">
        {parts || text}
      </p>
    </div>
  );
}
